import React, { useEffect, useMemo, useState } from "react";
import styles from "./NpsForm.module.scss";
import classNames from "classnames";
import { useInputHandler } from "../../../../../hooks/useInputHandler";
import { strings } from "../../../../../intl/fa";
import { useRequest } from "../../../../../hooks/useRequest";
import { sendSerajEventThunk } from "../../../../../core/seraj/seraj.thunk";
import { toast } from "react-toastify";
import { isPositiveNPS } from "utils/utils";
import { setOverallNPSThunk } from "core/nps/nps.thunk";
import { TextField } from "@bale/ui";
import { Slider } from "components/commons/slider/Slider";

const scoreText = {
  1: strings.nps.score_senses.never_recommend,
  2: strings.nps.score_senses.never_recommend,
  3: strings.nps.score_senses.not_recommend,
  4: strings.nps.score_senses.not_recommend,
  5: strings.nps.score_senses.not_sure,
  6: strings.nps.score_senses.not_sure,
  7: strings.nps.score_senses.recommend,
  8: strings.nps.score_senses.recommend,
  9: strings.nps.score_senses.definitely_recommend,
  10: strings.nps.score_senses.definitely_recommend,
};

type scoreTextKeyType = keyof typeof scoreText;
type selectedOptionType = { text: string; id: number }[];

const defaultPros = {
  1: strings.nps.default_pros.ux,
  2: strings.nps.default_pros.support,
  3: strings.nps.default_pros.efficiency,
  4: strings.nps.default_pros.price,
  5: strings.nps.default_pros.reports,
  6: strings.nps.default_pros.others,
};
const defaultCons = {
  1: strings.nps.default_cons.ux,
  2: strings.nps.default_cons.support,
  3: strings.nps.default_cons.efficiency,
  4: strings.nps.default_cons.price,
  5: strings.nps.default_cons.time,
  6: strings.nps.default_cons.others,
};

// based on seraj
export enum NpsTypes {
  "AdReportNPS" = 1,
  "OverallNPS" = 2,
}

interface Props {
  adId?: string;
  submit: boolean;
  onSubmit?: () => void;
  score?: number;
  onScoreChange?: (score: number) => void;
}
export const NpsForm = (props: Props) => {
  const { sendRequest: sendNPSSerajEvent } = useRequest(sendSerajEventThunk);
  const { sendRequest: sendOverallNPS } = useRequest(setOverallNPSThunk);
  const otherSuggestionsInputHandler = useInputHandler();
  const [selectedOptions, setSelectedOptions] = useState<selectedOptionType>(
    []
  );

  // should show textfield if '6:others' is selected
  const showOtherSuggestionInput = selectedOptions.find(
    (option) => option.id === 6
  );
  const isPositive = isPositiveNPS(props.score);
  const npsType =
    props.adId === undefined ? NpsTypes.OverallNPS : NpsTypes.AdReportNPS;

  const sendEvent = () => {
    if (props.score) {
      let badge_arr: number[] = [0, 0];
      selectedOptions.forEach((selectedOption, index) => {
        badge_arr[index] = selectedOption.id;
      });

      const description = showOtherSuggestionInput
        ? otherSuggestionsInputHandler.rawValue
        : "";

      const data = {
        ad_score: props.score,
        order_id: props.adId || "",
        page_type: npsType,
        badge_arr: badge_arr,
        description: description,
      };
      sendNPSSerajEvent({ data });
      sendOverallNPS();
      toast.info(strings.nps.completion_message);
    }
  };

  useEffect(() => {
    if (props.submit) {
      sendEvent();
      props.onSubmit?.();
    }
  }, [props.submit]);

  const optionClickHandler = (optionId: number, text: string) => {
    setSelectedOptions((prev) => {
      const found = prev.find((item) => item.id === optionId);
      if (found?.id) {
        return prev.filter((option) => option.id !== optionId);
      } else {
        if (prev.length >= 2) return prev;
        return [...prev, { text: text, id: optionId }];
      }
    });
  };

  const sliderChangeHandler = (event: React.ChangeEvent) => {
    const value = (event.target as HTMLInputElement).valueAsNumber;
    if (!isPositive === isPositiveNPS(value)) {
      setSelectedOptions([]);
    }
    props.onScoreChange?.(value);
  };

  const defaultProsOptions = useMemo(
    () =>
      Object.entries(defaultPros).map(([num, pros]) => {
        const isActive = selectedOptions.find((option) => option.id === +num);
        return (
          <div
            key={num}
            className={classNames(styles.Option, {
              [styles.active]: isActive,
            })}
            onClick={() => optionClickHandler(+num, pros)}
          >
            {pros}
          </div>
        );
      }),
    [selectedOptions]
  );

  const defaultConsOptions = useMemo(
    () =>
      Object.entries(defaultCons).map(([num, cons]) => {
        const isActive = selectedOptions.find((option) => option.id === +num);
        return (
          <div
            key={num}
            className={classNames(styles.Option, {
              [styles.active]: isActive,
            })}
            onClick={() => optionClickHandler(+num, cons)}
          >
            {cons}
          </div>
        );
      }),
    [selectedOptions]
  );

  return (
    <div className={styles.Container}>
      <label className={styles.Description} htmlFor="nps">
        {strings.nps.description}
      </label>
      <Slider
        min={1}
        max={10}
        value={String(props.score) || ""}
        onChange={sliderChangeHandler}
      />
      {props.score && (
        <>
          <div className={styles.ScoreText}>
            {scoreText[props.score as scoreTextKeyType]}
          </div>
          <div className={styles.NPSQuestion}>
            {isPositive ? strings.nps.pros_question : strings.nps.cons_question}
          </div>
          {isPositive ? (
            <div className={styles.OptionsContainer}>{defaultProsOptions}</div>
          ) : (
            <div className={styles.OptionsContainer}>{defaultConsOptions}</div>
          )}
          {showOtherSuggestionInput && (
            <TextField
              className={styles.TextField}
              {...otherSuggestionsInputHandler}
              placeholder={strings.nps.suggestion_placeholder}
              label={
                isPositive ? strings.nps.other_pros : strings.nps.other_cons
              }
              dir="rtl"
              multiline={true}
            />
          )}
        </>
      )}
    </div>
  );
};
